import { useMemo } from "react";
import { Download } from "lucide-react";
import Sparkline from "./Sparkline";
import { bucketByDay, fmtParam } from "@/lib/adminUtils";

export default function SummaryAndExport({ contacts, quotes, range }) {
  const all = useMemo(() => [
    ...contacts.map((r) => ({ ...r, kind: "contact" })),
    ...quotes.map((r) => ({ ...r, kind: "quote", service: r.service_slug, message: r.project_details })),
  ], [contacts, quotes]);

  const now = Date.now();
  const within = (days) => all.filter((r) => now - new Date(r.timestamp).getTime() <= days * 86400000).length;
  const sourceStats = useMemo(() => {
    const m = {};
    all.forEach((r) => {
      const s = r.source || "direct";
      if (!m[s]) m[s] = [];
      m[s].push(r);
    });
    return Object.entries(m)
      .map(([s, rows]) => ({ source: s, count: rows.length, trend: bucketByDay(rows, 7) }))
      .sort((a, b) => b.count - a.count);
  }, [all]);
  const topSources = sourceStats.slice(0, 5);
  const openCount = all.filter((r) => !r.contacted).length;
  const filtered = !!(range?.from || range?.to);

  const exportCsv = () => {
    const cols = ["kind", "timestamp", "name", "email", "country", "service", "source", "contacted", "message"];
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [cols.join(",")].concat(all.map((r) => cols.map((c) => escape(r[c])).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const suffix = filtered ? `-${fmtParam(range.from) || "x"}_to_${fmtParam(range.to) || "x"}` : "";
    a.download = `tri-axis-leads-${new Date().toISOString().slice(0, 10)}${suffix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const Stat = ({ k, v, hue = "blue" }) => (
    <div className="bg-white p-5">
      <div className="display text-3xl font-extrabold tracking-tighter" style={{ color: hue === "emerald" ? "#065F46" : "#1D4ED8" }}>{k}</div>
      <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mt-1">{v}</div>
    </div>
  );

  return (
    <section data-testid="admin-summary" className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Conversion summary</div>
        <button onClick={exportCsv} data-testid="export-csv-btn" className="inline-flex items-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-4 py-2">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
        <Stat k={within(7)} v="Last 7 days" />
        <Stat k={within(30)} v="Last 30 days" />
        <Stat k={all.length} v={filtered ? "In range" : "All time"} />
        <Stat k={openCount} v="Awaiting contact" hue="emerald" />
      </div>

      {topSources.length > 0 && (
        <div data-testid="source-trends" className="mt-4 border border-slate-200 bg-white">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-700">Top lead sources · 7-day trend</div>
            <div className="mono text-[10px] tracking-[0.18em] uppercase text-slate-400">oldest → today</div>
          </div>
          <ul className="divide-y divide-slate-100">
            {topSources.map(({ source, count, trend }) => {
              const total7 = trend.reduce((a, b) => a + b, 0);
              return (
                <li key={source} data-testid={`source-row-${source}`} className="flex items-center justify-between px-4 py-3 gap-4">
                  <div className="flex items-baseline gap-3 min-w-0">
                    <span className="mono text-[11px] tracking-[0.14em] uppercase text-topo truncate">{source}</span>
                    <span className="mono text-[10px] tracking-[0.18em] uppercase text-slate-400">{count} total</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Sparkline points={trend} />
                    <span className="mono text-[10px] tracking-[0.18em] uppercase text-emerald-700 w-16 text-right">
                      {total7} / 7d
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
